import { AlertTriangle, Inbox } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { WidgetDensity, WidgetView } from '../types'

export type WidgetItem = {
  id: string
  title: ReactNode
  subtitle?: ReactNode
  meta?: ReactNode
  trailing?: ReactNode
  leading?: ReactNode
  to?: string
  muted?: boolean
}

type QueryLike = { isLoading: boolean; isError: boolean; refetch: () => unknown }

/** Yükleniyor / hata / boş durumlarını widget gövdesine sığacak kompakt biçimde gösterir. */
export function WidgetState({ query, empty, emptyText, children }: { query: QueryLike; empty: boolean; emptyText: string; children: ReactNode }) {
  const { t } = useTranslation()
  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-9" />
        ))}
      </div>
    )
  }
  if (query.isError) {
    return (
      <div role="alert" className="flex h-full flex-col items-center justify-center gap-2 py-3 text-center">
        <AlertTriangle className="size-4 text-destructive" aria-hidden />
        <p className="text-xs text-muted-foreground">{t('errors.generic')}</p>
        <Button variant="outline" size="sm" className="h-7" onClick={() => void query.refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    )
  }
  if (empty) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-3 text-center text-muted-foreground">
        <Inbox className="size-4" aria-hidden />
        <p className="text-xs">{emptyText}</p>
      </div>
    )
  }
  return <>{children}</>
}

function ItemBody({ item }: { item: WidgetItem }) {
  const title = item.to ? (
    <Link to={item.to} className="hover:underline">
      {item.title}
    </Link>
  ) : (
    item.title
  )
  return (
    <div className="min-w-0 flex-1">
      <p className={cn('truncate text-[13px] font-medium', item.muted && 'text-muted-foreground line-through')}>{title}</p>
      {item.subtitle ? <p className="truncate text-xs text-muted-foreground">{item.subtitle}</p> : null}
    </div>
  )
}

export function WidgetItems({ items, view, density }: { items: WidgetItem[]; view: WidgetView; density: WidgetDensity }) {
  const compact = density === 'compact'

  if (view === 'card') {
    return (
      <ul className={cn('grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))]', compact ? 'gap-1.5' : 'gap-2')}>
        {items.map((item) => (
          <li key={item.id} className={cn('flex min-w-0 flex-col gap-2 rounded-xl border bg-muted/30 dark:bg-muted/15', compact ? 'p-2' : 'p-3')}>
            <div className="flex min-w-0 items-start gap-2">
              {item.leading}
              <ItemBody item={item} />
            </div>
            {item.meta || item.trailing ? (
              <div className="flex flex-wrap items-center justify-between gap-1.5 text-xs text-muted-foreground">
                {item.meta}
                {item.trailing}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    )
  }

  return (
    <ul className="divide-y divide-border/60">
      {items.map((item) => (
        <li key={item.id} className={cn('flex min-w-0 items-center gap-3', compact ? 'py-1.5' : 'py-2.5')}>
          {item.leading}
          <ItemBody item={item} />
          {item.meta ? <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{item.meta}</span> : null}
          {item.trailing ? <span className="shrink-0">{item.trailing}</span> : null}
        </li>
      ))}
    </ul>
  )
}
